/**
 * Admin Notification Service
 * Bridges GA4 events + Supabase real-time changes into admin in-app notifications.
 * Scores "hot leads" based on engagement signals.
 */

import { supabase } from '../lib/supabase';

// ─── Hot Lead Scoring ─────────────────────────────────────────────────────────

/**
 * Score a lead/inquiry based on engagement signals.
 * Returns { score: number, label: string, color: string }
 */
export const scoreHotLead = (data = {}) => {
  let score = 0;

  // High-value property price
  const price = data?.property_price || data?.price || 0;
  if (price >= 100_000_000) score += 40;       // 100M+ XOF
  else if (price >= 50_000_000) score += 25;
  else if (price >= 20_000_000) score += 10;

  // Preferred contact time (immediate = hotter)
  if (data?.preferred_contact_time === 'immediate' || data?.preferred_contact_time === 'morning') score += 20;

  // Has phone number (more committed)
  if (data?.phone) score += 15;

  // Came from ROI calc (investor intent)
  if (data?.lead_source === 'roi_calculator') score += 20;

  // Appointment booked (strongest signal)
  if (data?.type === 'appointment_booked') score += 35;

  // Inquiry with message (engaged)
  if (data?.message && data?.message?.length > 50) score += 10;

  if (score >= 60) return { score, label: 'Très Chaud 🔥', color: 'red', priority: 'critical' };
  if (score >= 35) return { score, label: 'Chaud ⚡', color: 'orange', priority: 'high' };
  if (score >= 15) return { score, label: 'Tiède', color: 'yellow', priority: 'medium' };
  return { score, label: 'Froid', color: 'blue', priority: 'low' };
};

// ─── Notification Types ───────────────────────────────────────────────────────

export const ADMIN_NOTIF_TYPES = {
  NEW_INQUIRY: 'new_inquiry',
  NEW_BOOKING: 'new_booking',
  APPOINTMENT_BOOKED: 'appointment_booked',
  PROPERTY_ACTION: 'property_action',
  HOT_LEAD: 'hot_lead',
  GENERATE_LEAD: 'generate_lead',
};

// ─── Fetch Recent Admin Notifications ────────────────────────────────────────

/**
 * Fetch recent inquiries, bookings, and appointments for admin notification feed.
 * Returns a unified, sorted list of notification objects.
 */
export const fetchAdminNotifications = async (limit = 50) => {
  try {
    const [inquiriesRes, appointmentsRes, bookingsRes] = await Promise.all([
      supabase
        ?.from('property_inquiries')
        ?.select(`
          id, created_at, status, message, phone, preferred_contact_time,
          client:user_profiles!client_id(full_name, email),
          property:properties(id, title, price, promotional_price)
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(limit),

      supabase
        ?.from('appointments')
        ?.select(`
          id, created_at, status, appointment_type, scheduled_date,
          user:user_profiles!user_id(full_name, email),
          property:properties(id, title, price)
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(limit),

      supabase
        ?.from('professional_bookings')
        ?.select(`
          id, created_at, status, service_type,
          client:user_profiles!client_id(full_name, email)
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(limit),
    ]);

    const notifications = [];

    // Map inquiries
    (inquiriesRes?.data || [])?.forEach((inq) => {
      const leadData = {
        price: inq?.property?.price || inq?.property?.promotional_price || 0,
        phone: inq?.phone,
        message: inq?.message,
        preferred_contact_time: inq?.preferred_contact_time,
      };
      const lead = scoreHotLead(leadData);
      notifications?.push({
        id: `inquiry-${inq?.id}`,
        sourceId: inq?.id,
        type: ADMIN_NOTIF_TYPES?.NEW_INQUIRY,
        priority: lead?.priority,
        leadScore: lead,
        title: 'Nouvelle Demande de Renseignement',
        message: `${inq?.client?.full_name || 'Visiteur'} — ${inq?.property?.title || 'Propriété'}`,
        detail: inq?.message || '',
        clientName: inq?.client?.full_name || 'Inconnu',
        clientEmail: inq?.client?.email || '',
        propertyTitle: inq?.property?.title || '',
        propertyPrice: inq?.property?.price || inq?.property?.promotional_price || 0,
        status: inq?.status,
        createdAt: inq?.created_at,
        isRead: false,
        actionPath: '/admin-dashboard',
        actionTab: 'dashboard',
        gaEvent: 'inquiry_created',
      });
    });

    // Map appointments
    (appointmentsRes?.data || [])?.forEach((apt) => {
      const leadData = {
        type: 'appointment_booked',
        price: apt?.property?.price || 0,
      };
      const lead = scoreHotLead(leadData);
      notifications?.push({
        id: `appointment-${apt?.id}`,
        sourceId: apt?.id,
        type: ADMIN_NOTIF_TYPES?.APPOINTMENT_BOOKED,
        priority: lead?.priority,
        leadScore: lead,
        title: 'Rendez-vous Réservé',
        message: `${apt?.user?.full_name || 'Client'} — ${apt?.property?.title || 'Propriété'}`,
        detail: `Type: ${apt?.appointment_type || ''} | Date: ${apt?.scheduled_date ? new Date(apt.scheduled_date)?.toLocaleDateString('fr-FR') : 'À définir'}`,
        clientName: apt?.user?.full_name || 'Inconnu',
        clientEmail: apt?.user?.email || '',
        propertyTitle: apt?.property?.title || '',
        propertyPrice: apt?.property?.price || 0,
        status: apt?.status,
        createdAt: apt?.created_at,
        isRead: false,
        actionPath: '/admin-dashboard',
        actionTab: 'appointments',
        gaEvent: 'appointment_booked',
      });
    });

    // Map professional bookings
    (bookingsRes?.data || [])?.forEach((bk) => {
      notifications?.push({
        id: `booking-${bk?.id}`,
        sourceId: bk?.id,
        type: ADMIN_NOTIF_TYPES?.NEW_BOOKING,
        priority: 'medium',
        leadScore: { label: 'Réservation', color: 'blue', score: 20 },
        title: 'Nouvelle Réservation Professionnelle',
        message: `${bk?.client?.full_name || 'Client'} — ${bk?.service_type || 'Service'}`,
        detail: `Statut: ${bk?.status || 'pending'}`,
        clientName: bk?.client?.full_name || 'Inconnu',
        clientEmail: bk?.client?.email || '',
        propertyTitle: '',
        propertyPrice: 0,
        status: bk?.status,
        createdAt: bk?.created_at,
        isRead: false,
        actionPath: '/admin-dashboard',
        actionTab: 'professionals',
        gaEvent: 'booking_created',
      });
    });

    // Sort by date descending
    notifications?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return { data: notifications?.slice(0, limit), error: null };
  } catch (error) {
    console.error('Error fetching admin notifications:', error);
    return { data: [], error };
  }
};

// ─── Subscribe to real-time admin events ─────────────────────────────────────

/**
 * Subscribe to new inquiries, appointments, and bookings.
 * Calls onNew(notification) for each new event.
 * Returns unsubscribe function.
 */
export const subscribeToAdminEvents = (onNew) => {
  const channelName = `admin-events-${Date.now()}`;
  const channel = supabase?.channel(channelName);

  if (!channel) return () => {};

  channel
    ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'property_inquiries' }, async (payload) => {
      const row = payload?.new;
      // Enrich with property data
      let propertyTitle = '';
      let propertyPrice = 0;
      if (row?.property_id) {
        const { data } = await supabase?.from('properties')?.select('title, price, promotional_price')?.eq('id', row?.property_id)?.single();
        propertyTitle = data?.title || '';
        propertyPrice = data?.price || data?.promotional_price || 0;
      }
      const lead = scoreHotLead({ price: propertyPrice, phone: row?.phone, message: row?.message, preferred_contact_time: row?.preferred_contact_time });
      onNew?.({
        id: `inquiry-${row?.id}-${Date.now()}`,
        sourceId: row?.id,
        type: ADMIN_NOTIF_TYPES?.NEW_INQUIRY,
        priority: lead?.priority,
        leadScore: lead,
        title: lead?.priority === 'critical' ? '🔥 Lead Très Chaud — Nouvelle Demande' : 'Nouvelle Demande de Renseignement',
        message: `${row?.client_name || row?.client_email || 'Visiteur'} — ${propertyTitle}`,
        detail: row?.message || '',
        clientName: row?.client_name || '',
        clientEmail: row?.client_email || '',
        propertyTitle,
        propertyPrice,
        status: row?.status,
        createdAt: row?.created_at || new Date()?.toISOString(),
        isRead: false,
        actionPath: '/admin-dashboard',
        actionTab: 'dashboard',
        gaEvent: 'inquiry_created',
      });
    })
    ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'appointments' }, async (payload) => {
      const row = payload?.new;
      let propertyTitle = '';
      let propertyPrice = 0;
      if (row?.property_id) {
        const { data } = await supabase?.from('properties')?.select('title, price')?.eq('id', row?.property_id)?.single();
        propertyTitle = data?.title || '';
        propertyPrice = data?.price || 0;
      }
      const lead = scoreHotLead({ type: 'appointment_booked', price: propertyPrice });
      onNew?.({
        id: `appointment-${row?.id}-${Date.now()}`,
        sourceId: row?.id,
        type: ADMIN_NOTIF_TYPES?.APPOINTMENT_BOOKED,
        priority: lead?.priority,
        leadScore: lead,
        title: 'Rendez-vous Réservé',
        message: `${propertyTitle || 'Propriété'} — ${row?.appointment_type || 'Visite'}`,
        detail: `Date: ${row?.scheduled_date ? new Date(row.scheduled_date)?.toLocaleDateString('fr-FR') : 'À définir'}`,
        clientName: '',
        clientEmail: '',
        propertyTitle,
        propertyPrice,
        status: row?.status,
        createdAt: row?.created_at || new Date()?.toISOString(),
        isRead: false,
        actionPath: '/admin-dashboard',
        actionTab: 'appointments',
        gaEvent: 'appointment_booked',
      });
    })
    ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'professional_bookings' }, (payload) => {
      const row = payload?.new;
      onNew?.({
        id: `booking-${row?.id}-${Date.now()}`,
        sourceId: row?.id,
        type: ADMIN_NOTIF_TYPES?.NEW_BOOKING,
        priority: 'medium',
        leadScore: { label: 'Réservation', color: 'blue', score: 20 },
        title: 'Nouvelle Réservation Professionnelle',
        message: `Service: ${row?.service_type || 'Professionnel'}`,
        detail: '',
        clientName: '',
        clientEmail: '',
        propertyTitle: '',
        propertyPrice: 0,
        status: row?.status,
        createdAt: row?.created_at || new Date()?.toISOString(),
        isRead: false,
        actionPath: '/admin-dashboard',
        actionTab: 'professionals',
        gaEvent: 'booking_created',
      });
    });

  channel?.subscribe();

  return () => {
    supabase?.removeChannel(channel);
  };
};

// ─── GA4 Event Listener ───────────────────────────────────────────────────────

/**
 * Intercept GA4 dataLayer pushes and call onGAEvent(eventName, params) for admin-relevant events.
 * Returns cleanup function.
 */
export const listenToGA4Events = (onGAEvent) => {
  if (typeof window === 'undefined') return () => {};

  window.dataLayer = window.dataLayer || [];
  const originalPush = window.dataLayer?.push?.bind(window.dataLayer);

  const ADMIN_EVENTS = new Set([
    'inquiry_created', 'appointment_booked', 'generate_lead',
    'property_view', 'roi_calculation', 'alert_signup',
  ]);

  window.dataLayer.push = function (...args) {
    args?.forEach((item) => {
      if (item && typeof item === 'object' && item?.[0] === 'event' && ADMIN_EVENTS?.has(item?.[1])) {
        try { onGAEvent?.(item?.[1], item?.[2] || {}); } catch (_) {}
      }
      // Also handle object-style pushes: { event: 'inquiry_created', ... }
      if (item && typeof item === 'object' && ADMIN_EVENTS?.has(item?.event)) {
        try { onGAEvent?.(item?.event, item); } catch (_) {}
      }
    });
    return originalPush(...args);
  };

  return () => {
    window.dataLayer.push = originalPush;
  };
};

export default {
  scoreHotLead,
  fetchAdminNotifications,
  subscribeToAdminEvents,
  listenToGA4Events,
  ADMIN_NOTIF_TYPES,
};
